/**
 * Self-contained SVG chart renderers for the data-chart figure types used by
 * the Mathematical Literacy and Accounting curricula (bar, grouped bar, line,
 * step, pie, box-and-whisker, tree, and shape/floor-plan diagrams).
 *
 * No external charting library — plain SVG so it stays inside the app's CSP
 * and matches the design system. Each renderer reads the loosely-typed fields
 * the backend now passes through (categories/series/steps/datasets/levels/…).
 */
import type { FigureSpec } from "../types";
import { saNumber } from "../lib/saNumber";

// Categorical series, ordered so the first two — the common case — are the
// palette's own ink and marker red. The rest step through the warm/cool range
// without reaching for the violet every generated chart starts with.
const PALETTE = ["#22354d", "#b23a2f", "#2f6b4f", "#b7791f", "#4a7b96", "#8a5a3c", "#6b7f4a"];
const AXIS = "#9aa4b2";
const GRID = "#dfe4ea";
const TEXT = "#4a5666";

function fmt(n: number): string {
  if (!isFinite(n)) return "";
  // A four-digit integer on an axis is almost always a year (2019, 2025),
  // and "2 019" reads as nonsense. Money and counts that large carry an
  // R prefix or a label, so the loss of grouping there is not felt.
  if (Number.isInteger(n) && n >= 1000 && n <= 2999) return String(n);
  return saNumber(n, 2);
}

/** Round a max value up to a "nice" axis bound and return tick values. */
function niceTicks(max: number, count = 5): number[] {
  if (max <= 0) return [0];
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return ticks;
}

function seriesLabel(s: any, i: number): string {
  return s?.label ?? s?.name ?? `Series ${i + 1}`;
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="chart-legend">
      {items.map((it, i) => (
        <span key={i} className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- bar chart
function BarChart({ spec }: { spec: FigureSpec }) {
  const cats = spec.categories ?? [];
  const series = (spec.series ?? []) as any[];
  if (!cats.length || !series.length) return null;
  const W = 560, H = 320, mL = 56, mR = 16, mT = 16, mB = 64;
  const pw = W - mL - mR, ph = H - mT - mB;
  const maxVal = Math.max(0, ...series.flatMap((s) => (s.values ?? []).map((v: number) => v)));
  const ticks = niceTicks(maxVal);
  const top = ticks[ticks.length - 1] || 1;
  const y = (v: number) => mT + ph - (v / top) * ph;
  const band = pw / cats.length;
  const gp = band * 0.2;
  const bw = (band - gp) / series.length;

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={spec.title ?? "bar chart"}>
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={mL} y1={y(t)} x2={W - mR} y2={y(t)} stroke={GRID} />
            <text x={mL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={TEXT}>{fmt(t)}</text>
          </g>
        ))}
        {cats.map((c, ci) => (
          <g key={ci}>
            {series.map((s, si) => {
              const v = s.values?.[ci] ?? 0;
              const x = mL + ci * band + gp / 2 + si * bw;
              return (
                <rect key={si} x={x + 1} y={y(v)} width={Math.max(1, bw - 2)} height={mT + ph - y(v)}
                  fill={PALETTE[si % PALETTE.length]} rx="2" />
              );
            })}
            <text x={mL + ci * band + band / 2} y={H - mB + 16} textAnchor="middle" fontSize="11" fill={TEXT}>
              {c}
            </text>
          </g>
        ))}
        <line x1={mL} y1={mT + ph} x2={W - mR} y2={mT + ph} stroke={AXIS} />
        {spec.y_label && (
          <text transform={`translate(14 ${mT + ph / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill={TEXT}>
            {spec.y_label}
          </text>
        )}
        {spec.x_label && (
          <text x={mL + pw / 2} y={H - 6} textAnchor="middle" fontSize="11" fill={TEXT}>{spec.x_label}</text>
        )}
      </svg>
      {series.length > 1 && (
        <Legend items={series.map((s, i) => ({ label: seriesLabel(s, i), color: PALETTE[i % PALETTE.length] }))} />
      )}
    </div>
  );
}

// --------------------------------------------------------------- line chart
function LineChart({ spec }: { spec: FigureSpec }) {
  const series = (spec.series ?? []) as any[];
  const pts = series.flatMap((s) => (s.points ?? []) as number[][]);
  if (!pts.length) return null;
  const W = 560, H = 320, mL = 60, mR = 16, mT = 16, mB = 52;
  const pw = W - mL - mR, ph = H - mT - mB;
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const xMin = spec.x_range?.[0] ?? Math.min(...xs);
  const xMax = spec.x_range?.[1] ?? Math.max(...xs);
  const yTicks = niceTicks(Math.max(...ys));
  const yTop = yTicks[yTicks.length - 1] || 1;
  const x = (v: number) => mL + ((v - xMin) / (xMax - xMin || 1)) * pw;
  const y = (v: number) => mT + ph - (v / yTop) * ph;
  const xTicks = niceTicks(xMax - xMin).map((t) => t + xMin);

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={spec.title ?? "line graph"}>
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={mL} y1={y(t)} x2={W - mR} y2={y(t)} stroke={GRID} />
            <text x={mL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={TEXT}>{fmt(t)}</text>
          </g>
        ))}
        {xTicks.map((t, i) => (
          <text key={i} x={x(t)} y={mT + ph + 16} textAnchor="middle" fontSize="11" fill={TEXT}>{fmt(t)}</text>
        ))}
        {series.map((s, si) => {
          const color = PALETTE[si % PALETTE.length];
          const p = (s.points ?? []) as number[][];
          const d = p.map((pt, i) => `${i ? "L" : "M"}${x(pt[0])} ${y(pt[1])}`).join(" ");
          return (
            <g key={si}>
              <path d={d} fill="none" stroke={color} strokeWidth="2.5" />
              {p.map((pt, i) => <circle key={i} cx={x(pt[0])} cy={y(pt[1])} r="3" fill={color} />)}
            </g>
          );
        })}
        <line x1={mL} y1={mT + ph} x2={W - mR} y2={mT + ph} stroke={AXIS} />
        <line x1={mL} y1={mT} x2={mL} y2={mT + ph} stroke={AXIS} />
        {spec.y_label && (
          <text transform={`translate(14 ${mT + ph / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill={TEXT}>
            {spec.y_label}
          </text>
        )}
        {spec.x_label && (
          <text x={mL + pw / 2} y={H - 4} textAnchor="middle" fontSize="11" fill={TEXT}>{spec.x_label}</text>
        )}
      </svg>
      {series.length > 1 && (
        <Legend items={series.map((s, i) => ({ label: seriesLabel(s, i), color: PALETTE[i % PALETTE.length] }))} />
      )}
    </div>
  );
}

// --------------------------------------------------------------- step chart
function StepChart({ spec }: { spec: FigureSpec }) {
  const steps = (spec.steps ?? []) as any[];
  if (!steps.length) return null;
  const W = 560, H = 300, mL = 56, mR = 16, mT = 16, mB = 52;
  const pw = W - mL - mR, ph = H - mT - mB;
  const xMin = Math.min(...steps.map((s) => s.range[0]));
  const xMax = Math.max(...steps.map((s) => s.range[1]));
  const yTicks = niceTicks(Math.max(...steps.map((s) => s.value)));
  const yTop = yTicks[yTicks.length - 1] || 1;
  const x = (v: number) => mL + ((v - xMin) / (xMax - xMin || 1)) * pw;
  const y = (v: number) => mT + ph - (v / yTop) * ph;

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={spec.title ?? "step graph"}>
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={mL} y1={y(t)} x2={W - mR} y2={y(t)} stroke={GRID} />
            <text x={mL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={TEXT}>{fmt(t)}</text>
          </g>
        ))}
        {steps.map((s, i) => {
          const color = PALETTE[0];
          const x1 = x(s.range[0]), x2 = x(s.range[1]), yy = y(s.value);
          const prev = steps[i - 1];
          return (
            <g key={i}>
              {prev && <line x1={x1} y1={y(prev.value)} x2={x1} y2={yy} stroke={color} strokeWidth="2" strokeDasharray="3 2" />}
              <line x1={x1} y1={yy} x2={x2} y2={yy} stroke={color} strokeWidth="3" />
            </g>
          );
        })}
        {[...steps.map((s) => s.range[0]), xMax].map((t, i) => (
          <text key={i} x={x(t)} y={mT + ph + 16} textAnchor="middle" fontSize="10" fill={TEXT}>{fmt(t)}</text>
        ))}
        <line x1={mL} y1={mT + ph} x2={W - mR} y2={mT + ph} stroke={AXIS} />
        {spec.y_label && (
          <text transform={`translate(14 ${mT + ph / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fill={TEXT}>
            {spec.y_label}
          </text>
        )}
        {spec.x_label && (
          <text x={mL + pw / 2} y={H - 4} textAnchor="middle" fontSize="11" fill={TEXT}>{spec.x_label}</text>
        )}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------- pie chart
function PieChart({ spec }: { spec: FigureSpec }) {
  const seg = (spec.segments ?? []) as any[];
  const data = seg.filter((s) => typeof s === "object" && !Array.isArray(s) && s.value != null);
  if (!data.length) return null;
  const total = data.reduce((a, s) => a + (s.value || 0), 0) || 1;
  const cx = 130, cy = 130, r = 110;
  let angle = -Math.PI / 2;
  const arcs = data.map((s, i) => {
    const frac = (s.value || 0) / total;
    const a0 = angle, a1 = angle + frac * 2 * Math.PI;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (a: number) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    const [x0, y0] = p(a0), [x1, y1] = p(a1);
    const pct = s.percentage != null ? s.percentage : Math.round(frac * 1000) / 10;
    return { d: `M${cx} ${cy} L${x0} ${y0} A${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`,
      color: PALETTE[i % PALETTE.length], label: s.label ?? "", pct, value: s.value };
  });

  return (
    <div className="chart chart-pie">
      <svg viewBox="0 0 260 260" width="220" role="img" aria-label={spec.title ?? "pie chart"}>
        {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} stroke="#fff" strokeWidth="1.5" />)}
      </svg>
      <div className="chart-pie-legend">
        {arcs.map((a, i) => (
          <div key={i} className="chart-legend-item">
            <span className="chart-legend-swatch" style={{ background: a.color }} />
            <span>{a.label} — <strong>{a.pct}%</strong> ({fmt(a.value)})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ box & whisker
function BoxPlot({ spec }: { spec: FigureSpec }) {
  const sets = (spec.datasets ?? []) as any[];
  const withData = sets.filter((d) => Array.isArray(d.five_number) && d.five_number.length === 5);
  if (!withData.length) return null;
  const W = 560, rowH = 46, mL = 110, mR = 24, mT = 16, mB = 40;
  const H = mT + withData.length * rowH + mB;
  const pw = W - mL - mR;
  const all = withData.flatMap((d) => d.five_number);
  const xMin = spec.x_range?.[0] ?? Math.min(...all);
  const xMax = spec.x_range?.[1] ?? Math.max(...all);
  const x = (v: number) => mL + ((v - xMin) / (xMax - xMin || 1)) * pw;
  const xTicks = niceTicks(xMax - xMin).map((t) => t + xMin);

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={spec.title ?? "box and whisker"}>
        {xTicks.map((t, i) => (
          <g key={i}>
            <line x1={x(t)} y1={mT} x2={x(t)} y2={H - mB} stroke={GRID} />
            <text x={x(t)} y={H - mB + 16} textAnchor="middle" fontSize="10" fill={TEXT}>{fmt(t)}</text>
          </g>
        ))}
        {withData.map((d, i) => {
          const [mn, q1, med, q3, mx] = d.five_number;
          const cy = mT + i * rowH + rowH / 2;
          const color = PALETTE[i % PALETTE.length];
          return (
            <g key={i}>
              <text x={mL - 10} y={cy + 4} textAnchor="end" fontSize="11" fill={TEXT}>{d.label}</text>
              <line x1={x(mn)} y1={cy} x2={x(q1)} y2={cy} stroke={color} strokeWidth="1.5" />
              <line x1={x(q3)} y1={cy} x2={x(mx)} y2={cy} stroke={color} strokeWidth="1.5" />
              <line x1={x(mn)} y1={cy - 8} x2={x(mn)} y2={cy + 8} stroke={color} strokeWidth="1.5" />
              <line x1={x(mx)} y1={cy - 8} x2={x(mx)} y2={cy + 8} stroke={color} strokeWidth="1.5" />
              <rect x={x(q1)} y={cy - 12} width={Math.max(1, x(q3) - x(q1))} height="24" fill={color} fillOpacity="0.25" stroke={color} rx="2" />
              <line x1={x(med)} y1={cy - 12} x2={x(med)} y2={cy + 12} stroke={color} strokeWidth="2.5" />
            </g>
          );
        })}
        {spec.x_label && <text x={mL + pw / 2} y={H - 4} textAnchor="middle" fontSize="11" fill={TEXT}>{spec.x_label}</text>}
      </svg>
    </div>
  );
}

// ------------------------------------------------------------- tree diagram
function TreeChart({ spec }: { spec: FigureSpec }) {
  const levels = (spec.levels ?? []) as any[];
  if (!levels.length) return null;
  // Enumerate leaves as the cartesian product of branch labels.
  let leaves: string[][] = [[]];
  for (const lv of levels) {
    const next: string[][] = [];
    for (const path of leaves) for (const b of lv.branches ?? []) next.push([...path, b]);
    leaves = next;
  }
  const nLeaves = leaves.length || 1;
  const W = 560, rowH = Math.min(34, 300 / nLeaves + 8);
  const H = Math.max(160, nLeaves * rowH + 40);
  const colW = (W - 40) / (levels.length + 1);
  const xAt = (c: number) => 20 + c * colW;
  const leafY = (i: number) => 24 + (i + 0.5) * ((H - 40) / nLeaves);

  // node y for a partial path at column c = average of the leaf ys it covers
  function nodeY(prefixLen: number, idx: number, groupSize: number) {
    const first = idx * groupSize, last = first + groupSize - 1;
    return (leafY(first) + leafY(last)) / 2;
  }
  const segs: JSX.Element[] = [];
  let key = 0;
  // draw level by level
  let groups = 1;
  for (let c = 0; c < levels.length; c++) {
    const branches = levels[c].branches ?? [];
    const groupSize = nLeaves / (groups * branches.length);
    for (let g = 0; g < groups; g++) {
      const parentY = nodeY(c, g, nLeaves / groups);
      const px = xAt(c);
      branches.forEach((b: string, bi: number) => {
        const childIdx = g * branches.length + bi;
        const cy = nodeY(c + 1, childIdx, groupSize);
        const cx = xAt(c + 1);
        segs.push(<line key={key++} x1={px} y1={parentY} x2={cx} y2={cy} stroke={AXIS} strokeWidth="1.4" />);
        segs.push(
          <text key={key++} x={(px + cx) / 2} y={(parentY + cy) / 2 - 4} textAnchor="middle" fontSize="11" fill={TEXT}>
            {b}
          </text>
        );
      });
    }
    groups *= branches.length;
  }
  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={spec.title ?? "tree diagram"}>
        {segs}
        <circle cx={xAt(0)} cy={nodeY(0, 0, nLeaves)} r="4" fill={PALETTE[0]} />
        {leaves.map((path, i) => (
          <text key={i} x={xAt(levels.length) + 6} y={leafY(i) + 4} fontSize="11" fill={TEXT}>
            {path.join(", ")}
          </text>
        ))}
      </svg>
      {(spec.outcomes != null || spec.favourable != null) && (
        <p className="chart-note">
          {spec.outcomes != null && <>Total outcomes: <strong>{spec.outcomes}</strong>. </>}
          {spec.favourable != null && <>Favourable: <strong>{spec.favourable}</strong>.</>}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------- spatial / floor plan info
function ShapeInfo({ spec }: { spec: FigureSpec }) {
  const dims = spec.dimensions ?? {};
  const entries = Object.entries(dims);
  return (
    <div className="chart chart-shape">
      {spec.description && <p className="chart-shape-desc">{spec.description}</p>}
      {entries.length > 0 && (
        <table className="figure-data-table">
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k}>
                <th style={{ textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</th>
                <td>{typeof v === "object" ? Object.entries(v as any).map(([a, b]) => `${a}: ${b}`).join(", ") : String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Returns a chart element for a supported chart type, or null. */
export function ChartFigure({ spec }: { spec: FigureSpec }): JSX.Element | null {
  switch (spec.type) {
    case "bar":
    case "bar_chart":
    case "grouped_bar_chart":
      return <BarChart spec={spec} />;
    case "line_graph":
      return <LineChart spec={spec} />;
    case "step_graph":
      return <StepChart spec={spec} />;
    case "pie_chart":
      return <PieChart spec={spec} />;
    case "box_and_whisker":
      return <BoxPlot spec={spec} />;
    case "tree_diagram":
      return <TreeChart spec={spec} />;
    case "spatial":
    case "floor_plan":
      return <ShapeInfo spec={spec} />;
    default:
      return null;
  }
}
